import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'

import CreateTripItem from '../Create'
import SearchTourOrCruise from '../Search/SearchTourOrCruise'

import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import LineEditAltIcon from 'LuxKit/Icons/line/LineEditAltIcon'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'

interface Props {
  tripId: string
  defaultDate?: string
  sectionId?: string
}

function SelectTourType(props: Props) {
  const { tripId, sectionId, defaultDate } = props

  const intl = useIntl()
  const openModal = useOpenModal()
  const { closeModal, goBack } = useModalCallbacks()

  const handleSearchTourClick = useCallback(() => {
    openModal(SearchTourOrCruise, { tripId, sectionId })
  }, [openModal, sectionId, tripId])

  const handleAddCustomTourClick = useCallback(() => {
    openModal(CreateTripItem, {
      itemType: 'TOURS' as const,
      tripId,
      defaultDate,
    })
  }, [defaultDate, openModal, tripId])

  const searchTitle = intl.formatMessage({
    id: 'QAK-x-yqfzfOxUYA_1e5',
    defaultMessage: 'Search Luxury Escape Offers',
    description: 'List item title for searching tour offers in trip planner',
  })
  const searchSubtitle = intl.formatMessage({
    id: 'HhAl1968YmuIdEGA9rDq',
    defaultMessage: 'Find the world’s best escapes, handpicked by experts',
    description: 'List item subtitle describing curated tour offers in trip planner',
  })
  const customTitle = intl.formatMessage({
    id: 'PjqF0z8n6VFPqx4a3Xlu',
    defaultMessage: 'Add your own tour',
    description: 'List item title for adding a custom tour in trip planner',
  })
  const customSubtitle = intl.formatMessage({
    id: '19bAcfJZE1bl1-8c7W6M',
    defaultMessage: 'Enter details of your own booking',
    description: 'List item subtitle for adding custom tour booking details in trip planner',
  })

  const listItemsData = useMemo(
    () => [
      {
        startIcon: <LineSearchIcon />,
        endIcon: <LineAngleRightIcon />,
        key: 'searchTour',
        title: searchTitle,
        subtitle: searchSubtitle,
        onClick: handleSearchTourClick,
      },
      {
        startIcon: <LineEditAltIcon />,
        endIcon: <LineAngleRightIcon />,
        key: 'addCustomAccommodation',
        title: customTitle,
        subtitle: customSubtitle,
        onClick: handleAddCustomTourClick,
      },
    ],
    [handleAddCustomTourClick, handleSearchTourClick, searchTitle, searchSubtitle, customTitle, customSubtitle],
  )

  return (
    <>
      <ModalHeader
        title={intl.formatMessage({
          id: '6xWEIpJJAaji00OxAa94',
          defaultMessage: 'Tour',
          description: 'Modal title for tour type selection in trip planner',
        })}
        onCloseButtonClick={closeModal}
        onBackButtonClick={goBack}
      />
      <ModalBody>
        <ModalContent>
          {listItemsData.map((item) => (
            <ClickableListItem
              key={item.key}
              startIcon={item.startIcon}
              endIcon={item.endIcon}
              title={item.title}
              subtitle={item.subtitle}
              onClick={item.onClick}
            />
          ))}
        </ModalContent>
      </ModalBody>
    </>
  )
}

export default SelectTourType
